import {
  SetStateAction,
  useMemo,
  useState,
  Dispatch,
  useEffect,
  useRef,
  useCallback,
} from "react";
import closeIcon from "assets/svg/close-icon.svg";
import {
  ArrowLeftIcon,
  AwardBadgeFilledIcon,
  AwardBadgeIcon,
  CalendarAddIcon,
  CalendarIcon,
  CalendarIconMain2,
  CardsIcon,
  CheckCircleBadgeIcon,
  ChevronRightIcon,
  ClockHistoryIcon,
  ErrorIcon,
  SendIcon,
} from "assets/icons";
import { GInput } from "components/UI/Inputs/GInput";
import { useInputValidation } from "hooks/useInputValidation";
import { Select, SelectProps, Switch } from "antd";
import { DatePicker } from "components/DataTable/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import eventImagePlaceholder from "assets/image/user-event-placeholder.jpg";
import { GTextArea } from "components/UI/Inputs/GTextArea";
import {
  useGetAgendaTypes,
  useGetEvent,
  useGetEvents,
  useCreateEvent,
  useAddSession,
  usePublishEvent,
  useAddLineup,
} from "hooks/query/useEvents";
import {
  AgendaInterface,
  AgendaType,
  EventItem,
  GetAgendaTypesResponse,
  GetEventResponse,
} from "interfaces";
import { Speaker } from "interfaces/ExternalPage/SpeakerLandingPage.interface";
import { useInviteToSpeak } from "hooks/query/useBookings";
import Loader from "components/UI/Loader";
import { useGetMyTeam } from "hooks/query/useAccount";
import { getUserDetails } from "components/TweekEditor/tweek-editor-utils/localStorageServices";
import { toast } from "sonner";
import { Loader as GMapLoader } from "@googlemaps/js-api-loader";
import instance from "lib/axiosInterceptor";
import { getRandomEventBanner } from "constants/event-banners";
import {
  EventLinkInterface,
  ConnectedAppsInterface,
  AppType,
  EventStatus,
} from "interfaces/interfaces-data";
import {
  setGmpPlaceholder,
  injectNoFocusRing,
  patchGmpPlaceAutocompleteShadowOnce,
  buildFriendlyAddress,
} from "components/UI/Modal/events/location-event-modal";
import teamsIcon from "assets/svg/teams-icon.svg";
import zoomAppIcon from "assets/svg/zoom-bg.svg";
import meetIcon from "assets/svg/meet-bg.svg";
import radioButtonUnfilled from "assets/svg/radio-button-unfilled.svg";
import radioButtonFilled from "assets/svg/radio-button-filled.svg";
import { data as currencyCodes } from "currency-codes";
import CurrencyFlag from "react-currency-flags";
import { useGetTeamInfo } from "hooks/query/useTeam";
import { getMeetingUrlError, isValidMeetingUrl } from "utils/meetingUrl";
import { getAppUrl } from "utils/subdomainDetection";

// Module-level loader singleton — avoids races if the drawer is mounted
// more than once and removes the need for function-object attachment.
let gmLoader: GMapLoader | null = null;

interface InviteSpeakerDrawerProps {
  open: boolean;
  onClose: () => void;
  speaker?: Speaker;
}

type EventDetailsFlow =
  | "inviteToSpeak"
  | "selectEvent"
  | "agendaDetails"
  | "selectAgenda"
  | "userFee"
  | "success";

export interface EventProposal {
  eventId: string;
  sessionId: string;
  speakingFormat: AgendaType;
  proposedTopic: string;
  description: string;
  duration: number;
  note: string;
  proposedAmount: number;
  currency: string;
}

export interface NewEventData {
  eventName: string;
  eventDate: string;
  eventLocation: string;
  googleCoordinates?: { lat: number; long: number };
  placeId?: string;
  tba?: boolean;
  eventLink?: EventLinkInterface | null;
}

export default function InviteSpeakerDrawer({
  open,
  onClose,
  speaker,
}: InviteSpeakerDrawerProps) {
  const [flow, setFlowState] = useState<EventDetailsFlow>("inviteToSpeak");
  const [previousFlow, setPreviousFlow] = useState<EventDetailsFlow | null>(
    null,
  );

  // Wrap setFlow so every flow change also records the prior value in
  // previousFlow. Replaces a useEffect-cleanup-based tracker.
  const setFlow = useCallback((value: SetStateAction<EventDetailsFlow>) => {
    setFlowState((prev) => {
      const next =
        typeof value === "function"
          ? (value as (p: EventDetailsFlow) => EventDetailsFlow)(prev)
          : value;
      setPreviousFlow(prev);
      return next;
    });
  }, []);
  const [eventProposal, setEventProposal] = useState<EventProposal>({
    eventId: "",
    sessionId: "",
    speakingFormat: "Keynote",
    proposedTopic: "",
    description: "",
    duration: 30,
    note: "",
    proposedAmount: 0,
    currency: speaker?.currency || "USD",
  });
  const [newEventData, setNewEventData] = useState<NewEventData>({
    eventName: "",
    eventDate: "",
    eventLocation: "",
  });
  const [invitationId, setInvitationId] = useState<string>("");
  const { data: teamInfo, isLoading: isTeamInfoLoading } = useGetTeamInfo();
  const teamId = teamInfo?.id || "";

  useEffect(() => {
    if (speaker?.currency) {
      setEventProposal((prev) =>
        prev.currency === speaker.currency
          ? prev
          : { ...prev, currency: speaker.currency as string },
      );
    }
  }, [speaker?.currency]);

  const eventQueryStartDate = dayjs().format("YYYY-MM-DD");
  const eventQueryEndDate = dayjs().add(100, "year").format("YYYY-MM-DD");

  const { data: eventsData, isLoading: isEventsLoading } = useGetEvents(
    {
      teamId,
      page: 1,
      pageLimit: 20,
      query: "",
      eventScope: "1",
      sortDirection: 1,
      startDate: eventQueryStartDate,
      endDate: eventQueryEndDate,
      range: "0",
    },
    Boolean(teamId),
  );
  const isEventsListLoading = isTeamInfoLoading || isEventsLoading;
  const { data: eventData, isLoading: isEventDataLoading } = useGetEvent(
    eventProposal.eventId,
    Boolean(eventProposal.eventId),
  );
  const { data: agendaTypesData } = useGetAgendaTypes();

  const upcomingEvents = useMemo(() => {
    if (!eventsData?.data?.events) return [];
    const now = dayjs();

    return eventsData.data.events.filter((event) => {
      const startDate = dayjs(event.startDate);
      return (
        startDate.isValid() &&
        (startDate.isAfter(now) || startDate.isSame(now)) &&
        event.status === EventStatus.Scheduled
      );
    });
  }, [eventsData]);

  // Confirm before closing mid-flow so the user doesn't silently lose work.
  // destroyOnClose in the parent wipes state, so no way back otherwise.
  const handleClose = useCallback(() => {
    if (flow !== "inviteToSpeak" && flow !== "success") {
      const confirmed = window.confirm(
        "You have unsaved changes. Are you sure you want to close?",
      );
      if (!confirmed) return;
    }
    onClose();
  }, [flow, onClose]);

  return (
    <section className="flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between py-3 px-5 border-b border-[#D5DBE1] bg-[#F5F5F5]">
        <h3 className="text-lg font-sfSemibold text-[#30313D]">
          Invite to speak
        </h3>
        <button
          onClick={handleClose}
          className="cursor-pointer"
          aria-label="Close"
        >
          <img src={closeIcon} alt="" />
        </button>
      </div>

      {/* Progress Indicator */}
      <ProgressIndicator flow={flow} hidden={flow === "success"} />

      {/* Content */}
      <div className="flex-1 px-5 pb-5">
        <section>
          {flow === "inviteToSpeak" && (
            <InviteToSpeak
              setFlow={setFlow}
              eventProposal={eventProposal}
              setEventProposal={setEventProposal}
              newEventData={newEventData}
              setNewEventData={setNewEventData}
              upcomingEvents={upcomingEvents}
            />
          )}
          {flow === "selectEvent" && (
            <SelectExistingEvent
              setFlow={setFlow}
              setEventProposal={setEventProposal}
              upcomingEvents={upcomingEvents}
              isEventsLoading={isEventsListLoading}
            />
          )}
          {flow === "agendaDetails" && (
            <AgendaDetails
              setFlow={setFlow}
              eventProposal={eventProposal}
              setEventProposal={setEventProposal}
              eventData={eventData}
              isEventDataLoading={isEventDataLoading}
              agendaTypesData={agendaTypesData}
            />
          )}
          {flow === "selectAgenda" && (
            <SelectAgenda
              setFlow={setFlow}
              setEventProposal={setEventProposal}
              eventData={eventData}
            />
          )}
          {flow === "userFee" && (
            <UserFee
              setFlow={setFlow}
              previousFlow={previousFlow}
              eventProposal={eventProposal}
              setEventProposal={setEventProposal}
              speaker={speaker}
              newEventData={newEventData}
              eventData={eventData}
              setInvitationId={setInvitationId}
            />
          )}
          {flow === "success" && (
            <Success onClose={onClose} invitationId={invitationId} />
          )}
        </section>
      </div>
    </section>
  );
}

function ProgressIndicator({
  flow,
  hidden,
}: {
  flow: EventDetailsFlow;
  hidden: boolean;
}) {
  if (hidden) return null;

  // Event details = picking or creating an event (inviteToSpeak, selectEvent)
  // Speaking details = picking or authoring the agenda (agendaDetails, selectAgenda)
  // Compensation = userFee
  const isSpeakingDetailsActive =
    flow === "agendaDetails" || flow === "selectAgenda" || flow === "userFee";
  const isCompensationActive = flow === "userFee";

  return (
    <div className={`${"grid grid-cols-2 h-[80px] pt-3 pb-8 px-5"}`}>
      <div className="w-full relative flex items-center">
        {/* Event details */}
        <div
          aria-hidden="true"
          className={`${
            isSpeakingDetailsActive ? "bg-primary" : "bg-[#E5E7EB]"
          } w-full h-[2px]`}
        />
        <div className="absolute left-0 top-[50%] -translate-y-5">
          <div className="w-10 aspect-square shadow flex items-center justify-center bg-white border border-[#EAECF0] rounded-lg">
            <CalendarIconMain2 />
          </div>
          <p className="text-sm font-sfMedium mt-0.5 text-primary">
            Event details
          </p>
        </div>

        {/* Speaking details */}
        <div
          className={`absolute right-0 top-[50%] -translate-y-5 flex flex-col justify-center items-center translate-x-[50%] z-10`}
        >
          <div className="w-10 aspect-square shadow flex items-center justify-center bg-white border border-[#EAECF0] rounded-lg">
            <CardsIcon
              stroke={`${isSpeakingDetailsActive ? "#FF6929" : "#757575"}`}
            />
          </div>
          <p
            className={`text-sm font-sfMedium mt-0.5
                    ${isSpeakingDetailsActive ? "text-primary" : ""}
                    `}
          >
            Speaking details
          </p>
        </div>
      </div>

      {/* Compensation */}
      <div className={`w-full relative flex items-center`}>
        <div
          aria-hidden="true"
          className={`${
            isCompensationActive ? "bg-primary" : "bg-[#E5E7EB]"
          } w-full h-[2px]`}
        />

        <div className="absolute right-0 top-[50%] -translate-y-5 flex flex-col justify-center items-end ">
          <div className="w-10 aspect-square shadow flex items-center justify-center bg-white border border-[#EAECF0] rounded-lg">
            <SendIcon
              stroke={`${isCompensationActive ? "#FF6929" : "#757575"}`}
            />
          </div>
          <p
            className={`text-sm font-sfMedium mt-0.5
                    ${isCompensationActive ? "text-primary" : ""}
                    `}
          >
            Compensation
          </p>
        </div>
      </div>
    </div>
  );
}

function InviteToSpeak({
  setFlow,
  eventProposal,
  setEventProposal,
  newEventData,
  setNewEventData,
  upcomingEvents,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  eventProposal: EventProposal;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
  newEventData: NewEventData;
  setNewEventData: Dispatch<SetStateAction<NewEventData>>;
  upcomingEvents: EventItem[];
}) {
  const [errorObject, setErrorObject] = useState({
    eventName: "",
    eventLocation: "",
    eventUrl: "",
    eventDate: "",
  });
  const { validateMultipleFields } = useInputValidation();

  // Location UI state
  const [locationTab, setLocationTab] = useState(0); // 0=Physical, 1=Virtual
  const inputWrapperRef = useRef<HTMLDivElement | null>(null);
  const [apps, setApps] = useState<ConnectedAppsInterface[]>([]);
  const [appActiveIndex, setAppActiveIndex] = useState(-1);
  const [customAppActive, setCustomAppActive] = useState<
    "zoom" | "meet" | "teams" | null
  >(null);
  const [mapsLoadFailed, setMapsLoadFailed] = useState(false);
  const [meetingLinkTouched, setMeetingLinkTouched] = useState(false);
  const [isEventDatePickerOpen, setIsEventDatePickerOpen] = useState(false);

  const defaultApps = [
    {
      name: "Teams",
      slug: "teams" as const,
      action: () => setCustomAppActive("teams"),
      icon: teamsIcon,
    },
    {
      name: "Zoom",
      slug: "zoom" as const,
      action: () => setCustomAppActive("zoom"),
      icon: zoomAppIcon,
    },
    {
      name: "Meet",
      slug: "meet" as const,
      action: () => setCustomAppActive("meet"),
      icon: meetIcon,
    },
  ];

  const todayStart = () => dayjs().startOf("day");

  // Disable picking a FROM date in the past
  const disableFromDate = (current: Dayjs) => {
    if (!current) return false;
    const isPast = current.startOf("day").isBefore(todayStart());
    return isPast;
  };

  const defaultStartTime = getDefaultStartTime(15);

  const duplicateEvent = useMemo(() => {
    const trimmed = newEventData.eventName.trim().toLowerCase();
    if (!trimmed) return null;
    return (
      upcomingEvents.find((e) => e.title.trim().toLowerCase() === trimmed) ||
      null
    );
  }, [upcomingEvents, newEventData.eventName]);
  const meetingLink = newEventData.eventLink?.link ?? "";
  const meetingLinkError = getMeetingUrlError(
    meetingLink,
    meetingLinkTouched || Boolean(errorObject.eventLocation),
  );

  // Fetch connected apps
  useEffect(() => {
    const getApps = async () => {
      try {
        const result = await instance.get(`/Apps`);
        setApps(result.data);
      } catch (e) {
        console.error("Failed to fetch connected apps", e);
        toast.error(
          "Couldn't load connected apps. You can still pick a meeting provider manually.",
        );
      }
    };
    getApps();
  }, []);

  // Google Places Autocomplete setup
  useEffect(() => {
    if (locationTab !== 0) return;

    let cancelled = false;

    if (!gmLoader) {
      gmLoader = new GMapLoader({
        apiKey: process.env.REACT_APP_GOOGLE_MAP_API as string,
        version: "beta",
        libraries: ["places"],
      });
    }

    let placeAutocompleteEl: any = null;
    let onSelect: any = null;

    const init = async () => {
      try {
        await gmLoader!.load();
        if (cancelled) return;

        const wrapper = inputWrapperRef.current;
        if (!wrapper || !window.google?.maps) return;

        const { PlaceAutocompleteElement } = (await google.maps.importLibrary(
          "places",
        )) as any;

        wrapper.querySelector('[data-gmp="place-autocomplete"]')?.remove();
        patchGmpPlaceAutocompleteShadowOnce();

        placeAutocompleteEl = new PlaceAutocompleteElement();
        placeAutocompleteEl.setAttribute("data-gmp", "place-autocomplete");

        wrapper.appendChild(placeAutocompleteEl);
        requestAnimationFrame(() => {
          setGmpPlaceholder(placeAutocompleteEl, "Enter your address here");
          injectNoFocusRing(placeAutocompleteEl);
        });

        onSelect = async (e: any) => {
          if (cancelled) return;

          const placePrediction = e?.placePrediction;
          if (!placePrediction) return;

          const place = placePrediction.toPlace();
          await place.fetchFields({
            fields: [
              "id",
              "displayName",
              "formattedAddress",
              "location",
              "addressComponents",
            ],
          });

          const placeId = place.id || "";
          const address = buildFriendlyAddress(place);

          const lat = place.location?.lat?.();
          const lng = place.location?.lng?.();

          setNewEventData((prev) => ({
            ...prev,
            eventLocation: address,
            placeId,
            googleCoordinates:
              lat != null && lng != null ? { lat, long: lng } : undefined,
          }));
        };

        placeAutocompleteEl.addEventListener("gmp-select", onSelect);
        if (!cancelled) setMapsLoadFailed(false);
      } catch (err) {
        console.error("Google Maps load failed", err);
        if (!cancelled) {
          setMapsLoadFailed(true);
          toast.error(
            "Address autocomplete is unavailable. You can type the location manually.",
          );
        }
      }
    };

    const raf = requestAnimationFrame(init);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (placeAutocompleteEl && onSelect) {
        placeAutocompleteEl.removeEventListener("gmp-select", onSelect);
      }
      placeAutocompleteEl?.remove();
      placeAutocompleteEl = null;
      onSelect = null;
    };
  }, [locationTab, setNewEventData]);

  const handleContinue = () => {
    // Compute a fresh default date at submit time so it doesn't go stale if
    // the drawer stays open for a while
    const finalEventDate =
      newEventData.eventDate ||
      getDefaultStartTime(15).format("YYYY-MM-DDTHH:mm:ss");
    if (finalEventDate !== newEventData.eventDate) {
      setNewEventData((prev) => ({ ...prev, eventDate: finalEventDate }));
    }

    // Build validation fields dynamically
    const validationFields: Record<string, any> = {
      eventName: {
        value: newEventData.eventName,
        type: "title",
        options: {
          fieldLabel: "Event name",
          minLength: 2,
          onError: (error: string) =>
            setErrorObject((prev) => ({
              ...prev,
              eventName: error,
            })),
          onSuccess: () =>
            setErrorObject((prev) => ({
              ...prev,
              eventName: "",
            })),
        },
      },
      eventDate: {
        value: finalEventDate,
        type: "required",
        options: {
          fieldLabel: "Event date",
          onError: (error: string) =>
            setErrorObject((prev) => ({
              ...prev,
              eventDate: error,
            })),
          onSuccess: () =>
            setErrorObject((prev) => ({
              ...prev,
              eventDate: "",
            })),
        },
      },
    };

    // Only validate location if not TBA
    if (!newEventData.tba) {
      if (locationTab === 0) {
        // Physical address tab - validate location string
        validationFields.eventLocation = {
          value: newEventData.eventLocation,
          type: "title",
          options: {
            fieldLabel: "Event location",
            minLength: 2,
            onError: (error: string) =>
              setErrorObject((prev) => ({
                ...prev,
                eventLocation: error,
              })),
            onSuccess: () =>
              setErrorObject((prev) => ({
                ...prev,
                eventLocation: "",
              })),
          },
        };
      } else {
        setMeetingLinkTouched(true);
        if (!isValidMeetingUrl(meetingLink)) {
          setErrorObject((prev) => ({
            ...prev,
            eventLocation: getMeetingUrlError(meetingLink, true),
          }));
          return;
        }
        setErrorObject((prev) => ({ ...prev, eventLocation: "" }));
      }
    } else {
      // Clear location error if TBA
      setErrorObject((prev) => ({ ...prev, eventLocation: "" }));
    }

    const isValid = validateMultipleFields(validationFields);
    if (!isValid) {
      return;
    }

    setFlow("agendaDetails");
  };

  return (
    <div className="grid grid-cols-1 gap-4 pt-3">
      {/* Invite to existing event */}
      <div
        className="bg-[#F5F5F5] w-full p-2 flex items-center gap-2 justify-between rounded-lg cursor-pointer"
        onClick={() => {
          setFlow("selectEvent");
        }}
      >
        <div className="">
          <h4 className="text-base font-sfMedium text-[#20242A] mb-2">
            Invite speaker to existing event
          </h4>
          <p className="text-sm text-[#20242A] font-sfRegular">
            Select from your list of events
          </p>
        </div>
        <button className="">
          <ChevronRightIcon />
        </button>
      </div>

      {/* OR divider */}
      <div className="flex items-center gap-4 py-2 pb-1">
        <div className="flex-1 h-px bg-[#EAECF0]"></div>
        <span className="text-sm text-[#667085] font-sfMedium">OR</span>
        <div className="flex-1 h-px bg-[#EAECF0]"></div>
      </div>

      {/* Create new event form */}
      <div className="grid grid-cols-1 gap-4 w-full">
        {/* Event name */}
        <div>
          <GInput
            required
            inputValue={newEventData.eventName}
            setInput={(e) => {
              setNewEventData((prev) => ({ ...prev, eventName: e }));
              // Typing a new event name signals new-event intent — clear any
              // previously-selected existing event so isNewEventFlow resolves
              // correctly on submit
              if (eventProposal.eventId) {
                setEventProposal((prev) => ({ ...prev, eventId: "" }));
              }
            }}
            label="Event name"
            placeholder="Enter event name"
            error={errorObject.eventName}
            inputBackgroundColor="#F5F5F5"
          />
          {duplicateEvent && !errorObject.eventName && (
            <p className="text-[#B54708] text-xs mt-1 font-sfRegular">
              You already have an upcoming event with this name — submitting
              will create a duplicate.
            </p>
          )}
        </div>

        {/* Event date */}
        <div>
          <label className="block text-sm font-sfMedium text-[#4B5563] mb-2">
            Event date
          </label>
          <div
            className="w-full relative bg-[#F5F5F5] rounded-lg p-3 flex items-center justify-between cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => setIsEventDatePickerOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setIsEventDatePickerOpen(true);
              }
            }}
          >
            <div className="absolute opacity-0 inset-0">
              <DatePicker
                open={isEventDatePickerOpen}
                onOpenChange={setIsEventDatePickerOpen}
                disabledDate={disableFromDate}
                value={
                  newEventData.eventDate ? dayjs(newEventData.eventDate) : null
                }
                onChange={(date) => {
                  if (date) {
                    setNewEventData((prev) => ({
                      ...prev,
                      eventDate: date.format("YYYY-MM-DDTHH:mm:ss"),
                    }));
                    setIsEventDatePickerOpen(false);
                  }
                }}
                inputReadOnly
              />
            </div>
            <p>
              {newEventData.eventDate
                ? dayjs(newEventData.eventDate).format("MMM DD YYYY")
                : dayjs(defaultStartTime).format("MMM DD YYYY")}
            </p>

            <CalendarAddIcon />
          </div>
          {errorObject.eventDate && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <ErrorIcon />
              <p className="text-[#F04438] text-xs lg:text-sm font-sfRegular">
                {errorObject.eventDate}
              </p>
            </div>
          )}
        </div>

        {/* Event location - inline location UI */}
        <div>
          <label className="block text-sm font-sfMedium text-[#4B5563] mb-2">
            Event location
          </label>

          {/* TBA toggle */}
          <div className="flex justify-between mb-2">
            <p className="text-[#3D4045] font-sfRegular text-sm">Set as TBA</p>
            <Switch
              checked={newEventData.tba || false}
              onChange={(val) => {
                setNewEventData((prev) =>
                  val
                    ? {
                        ...prev,
                        tba: true,
                        eventLocation: "",
                        placeId: undefined,
                        googleCoordinates: undefined,
                        eventLink: null,
                      }
                    : { ...prev, tba: false },
                );
                setErrorObject((prev) => ({ ...prev, eventLocation: "" }));
              }}
            />
          </div>

          {!newEventData.tba && (
            <>
              {/* Tab bar */}
              <div className="flex items-center mb-3">
                <button
                  onClick={() => {
                    setLocationTab(0);
                    // Leaving the Virtual tab — clear its state
                    setNewEventData((prev) => ({
                      ...prev,
                      eventLink: null,
                    }));
                    setAppActiveIndex(-1);
                    setCustomAppActive(null);
                    setErrorObject((prev) => ({ ...prev, eventLocation: "" }));
                  }}
                  className={`flex items-center justify-center py-2.5 w-full ${
                    locationTab === 0
                      ? "border-b-2 border-[#FF6929]"
                      : "border-b-2 border-[#F3F0EB]"
                  }`}
                >
                  <p
                    className={`whitespace-nowrap text-sm ${
                      locationTab === 0
                        ? "text-[#FF6929] font-sfSemibold"
                        : "text-[#666563] font-sfRegular"
                    }`}
                  >
                    Physical Address
                  </p>
                </button>
                <button
                  onClick={() => {
                    setLocationTab(1);
                    // Leaving the Physical tab — clear its state
                    setNewEventData((prev) => ({
                      ...prev,
                      eventLocation: "",
                      placeId: undefined,
                      googleCoordinates: undefined,
                    }));
                    setErrorObject((prev) => ({ ...prev, eventLocation: "" }));
                  }}
                  className={`flex items-center justify-center py-2.5 w-full ${
                    locationTab === 1
                      ? "border-b-2 border-[#FF6929]"
                      : "border-b-2 border-[#F3F0EB]"
                  }`}
                >
                  <p
                    className={`whitespace-nowrap text-sm ${
                      locationTab === 1
                        ? "text-[#FF6929] font-sfSemibold"
                        : "text-[#666563] font-sfRegular"
                    }`}
                  >
                    Virtual
                  </p>
                </button>
              </div>

              {/* Physical Address tab */}
              {locationTab === 0 && (
                <>
                  {mapsLoadFailed ? (
                    <GInput
                      inputValue={newEventData.eventLocation}
                      setInput={(e) =>
                        setNewEventData((prev) => ({
                          ...prev,
                          eventLocation: e,
                          placeId: undefined,
                          googleCoordinates: undefined,
                        }))
                      }
                      placeholder="Enter your address here"
                      inputBackgroundColor="#F5F5F5"
                    />
                  ) : (
                    <>
                      <div ref={inputWrapperRef} />
                      {newEventData.eventLocation && (
                        <p className="text-sm text-[#344054] mt-2 font-sfRegular">
                          {newEventData.eventLocation}
                        </p>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Virtual tab */}
              {locationTab === 1 && (
                <div>
                  {/* {apps.length ? (
                    <>
                      <div role="radiogroup" aria-label="Select meeting app">
                        {apps.map((item, index) => (
                          <button
                            type="button"
                            role="radio"
                            aria-checked={appActiveIndex === index}
                            onClick={() => {
                              setAppActiveIndex((prev) =>
                                prev === index ? -1 : index,
                              );
                              setNewEventData((prev) => ({
                                ...prev,
                                eventLink: {
                                  app:
                                    item.app === AppType.Microsoft_Teams
                                      ? "Teams"
                                      : item.app === AppType.Google_Meet
                                        ? "Meet"
                                        : "Zoom",
                                  link: prev.eventLink?.link || "",
                                },
                              }));
                            }}
                            className={`border ${
                              appActiveIndex === index
                                ? "border-[#FF6929] bg-[#FFF0EA]"
                                : "border-[#EAECF0] bg-[#FFFFFF]"
                            } p-2 flex items-center justify-between rounded-xl w-full mb-1`}
                            key={item.id}
                          >
                            <div className="flex items-center gap-2">
                              <img
                                src={
                                  item.app === AppType.Microsoft_Teams
                                    ? teamsIcon
                                    : item.app === AppType.Google_Meet
                                      ? meetIcon
                                      : zoomAppIcon
                                }
                                alt=""
                              />
                              <p className="text-[#344054] font-sfMedium">
                                {item.app === AppType.Microsoft_Teams
                                  ? "Microsoft Teams"
                                  : item.app === AppType.Google_Meet
                                    ? "Google Meet"
                                    : "Zoom"}
                              </p>
                            </div>
                            <img
                              src={
                                appActiveIndex === index
                                  ? radioButtonFilled
                                  : radioButtonUnfilled
                              }
                              alt=""
                            />
                          </button>
                        ))}
                      </div>
                      <div className="mt-2">
                        <GInput
                          inputValue={newEventData.eventLink?.link ?? ""}
                          label="Meeting link"
                          placeholder="Paste the meeting link here"
                          setInput={(e) => {
                            setNewEventData((prev) => ({
                              ...prev,
                              eventLink: prev.eventLink
                                ? { ...prev.eventLink, link: e }
                                : { app: "", link: e },
                            }));
                          }}
                        />
                      </div>
                    </>
                  ) : ( */}
                  <div>
                    <p className="text-[#101828] text-sm font-sfMedium my-2">
                      Which app is the meeting taking place?
                    </p>
                    <div
                      className="flex items-center gap-2"
                      role="radiogroup"
                      aria-label="Select meeting app"
                    >
                      {defaultApps.map((item) => (
                        <button
                          key={item.slug}
                          type="button"
                          role="radio"
                          aria-checked={
                            customAppActive === item.slug ||
                            !!newEventData.eventLink?.app
                              ?.toLowerCase()
                              ?.includes(item.slug)
                          }
                          onClick={() => {
                            setNewEventData((prev) => ({
                              ...prev,
                              eventLink: prev.eventLink
                                ? { ...prev.eventLink, app: item.name }
                                : { app: item.name, link: "" },
                            }));
                            item.action();
                          }}
                          className={`border ${
                            customAppActive === item.slug ||
                            newEventData.eventLink?.app
                              ?.toLowerCase()
                              ?.includes(item.slug)
                              ? "border-[#FF6929] bg-[#FFF0EA]"
                              : "border-[#EAECF0] bg-[#FFFFFF]"
                          } p-2 flex items-center justify-between rounded-xl w-full mb-1`}
                        >
                          <div className="flex items-center gap-2">
                            <img src={item.icon} alt="" />
                            <p className="text-[#344054] font-sfMedium">
                              {item.name}
                            </p>
                          </div>
                          <img
                            src={
                              customAppActive === item.slug ||
                              newEventData.eventLink?.app
                                ?.toLowerCase()
                                ?.includes(item.slug)
                                ? radioButtonFilled
                                : radioButtonUnfilled
                            }
                            alt=""
                          />
                        </button>
                      ))}
                    </div>
                    <div className="mt-2">
                      <GInput
                        inputValue={newEventData.eventLink?.link ?? ""}
                        label="Meeting link"
                        placeholder="Paste the meeting link here"
                        error={meetingLinkError}
                        onBlur={() => setMeetingLinkTouched(true)}
                        setInput={(e) => {
                          setNewEventData((prev) => ({
                            ...prev,
                            eventLink: prev.eventLink
                              ? { ...prev.eventLink, link: e }
                              : { app: "", link: e },
                          }));
                        }}
                      />
                    </div>
                  </div>
                  {/* )} */}
                </div>
              )}
            </>
          )}

          {locationTab === 0 && errorObject.eventLocation && (
            <div className="flex items-center gap-1.5 mt-1.5">
              <ErrorIcon />
              <p className="text-[#F04438] text-xs lg:text-sm font-sfRegular">
                {errorObject.eventLocation}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Continue button */}
      <div className="">
        <button
          onClick={() => handleContinue()}
          className="w-full bg-[#20242A] text-white rounded-lg py-3 font-sfMedium text-base hover:bg-[#3a3937] transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function SelectExistingEvent({
  setFlow,
  upcomingEvents,
  setEventProposal,
  isEventsLoading,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
  upcomingEvents: EventItem[];
  isEventsLoading: boolean;
}) {
  if (isEventsLoading) {
    return (
      <div className="h-full w-full">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <BackButton onClick={() => setFlow("inviteToSpeak")}></BackButton>

      <p className="mt-4 text-[#20242A] font-sfMedium text-base">
        Which event is this invitation for?
      </p>

      <div className="mt-4 grid grid-cols-1 gap-2">
        {upcomingEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CalendarIcon width={32} height={32} />
            <p className="text-sm text-[#667085] font-sfRegular mt-3 text-center">
              You don't have any upcoming events.
            </p>
            <button
              onClick={() => setFlow("inviteToSpeak")}
              className="mt-4 text-sm text-primary font-sfMedium underline"
            >
              Create a new event instead
            </button>
          </div>
        ) : (
          upcomingEvents.map((event) => (
            <EventCard
              key={event.id}
              setFlow={setFlow}
              event={event}
              setEventProposal={setEventProposal}
            />
          ))
        )}
      </div>
    </div>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={() => {
        onClick();
      }}
      className="border border-[#D0D5DD] py-2 pr-1 pl-2 rounded-[6px] flex items-center gap-2 text-[#344054] font-sfMedium text-base hover:bg-black hover:text-white transition-colors"
    >
      <ArrowLeftIcon stroke="currentColor" />
      Back
    </button>
  );
}

function EventCard({
  setFlow,
  event,
  setEventProposal,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  event: EventItem;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
}) {
  return (
    <div
      className="flex gap-3 p-3 bg-[#F5F5F5] rounded-2xl cursor-pointer hover:bg-[#E5E5E5] transition-colors"
      onClick={() => {
        setFlow("agendaDetails");
        setEventProposal((prev) => ({ ...prev, eventId: event.id }));
      }}
    >
      {/* Image */}
      <div className="w-[80px] h-[63.5px] rounded-[7px] overflow-hidden flex-shrink-0">
        <img
          src={event.banner || event.bannerUrl || eventImagePlaceholder}
          alt={"Event banner"}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h4 className="font-monaMedium text-[#000000] text-lg truncate">
          {event.title}
        </h4>

        {/* Date */}
        <div className="flex items-center gap-2 mt-3">
          <CalendarIcon width={14} height={14} />
          <span className="text-sm text-[#444D5A] font-monaMedium">
            {dayjs(event.startDate).format("MMM DD, YYYY")}
          </span>
        </div>
      </div>
    </div>
  );
}

function AgendaDetails({
  setFlow,
  eventData,
  isEventDataLoading,
  agendaTypesData,
  eventProposal,
  setEventProposal,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  eventProposal: EventProposal;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
  eventData?: GetEventResponse;
  isEventDataLoading?: boolean;
  agendaTypesData?: GetAgendaTypesResponse;
}) {
  const [agendaTypes, setAgendaTypes] = useState<string[]>([]);
  const [topicError, setTopicError] = useState("");
  const [durationError, setDurationError] = useState("");

  useEffect(() => {
    if (Array.isArray(agendaTypesData)) {
      setAgendaTypes([...agendaTypesData].sort());
    }
  }, [agendaTypesData]);

  if (isEventDataLoading) {
    return (
      <div className="h-full w-full">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <BackButton
        onClick={() =>
          setFlow(eventProposal.eventId ? "selectEvent" : "inviteToSpeak")
        }
      ></BackButton>

      <div className="grid grid-cols-1 gap-4 pt-3">
        {/* Assign existing agenda - only show when an existing event is selected */}
        {eventProposal.eventId &&
          (() => {
            const agendaCount = (eventData?.agenda ?? []).reduce(
              (acc, agenda) => acc + agenda.sessions.length,
              0,
            );
            const agendaText =
              agendaCount === 0
                ? "No agendas on this event yet"
                : agendaCount === 1
                  ? "You have 1 agenda on this event"
                  : `You have ${agendaCount} agendas on this event`;
            return (
              <>
                <div
                  className="bg-[#F5F5F5] w-full p-2 flex items-center gap-2 justify-between rounded-lg cursor-pointer"
                  onClick={() => {
                    setFlow("selectAgenda");
                  }}
                >
                  <div className="">
                    <h4 className="text-base font-sfMedium text-[#20242A] mb-2">
                      Assign existing agenda to speaker
                    </h4>
                    <p className="text-sm text-[#20242A] font-sfRegular">
                      {agendaText}
                    </p>
                  </div>
                  <button className="">
                    <ChevronRightIcon />
                  </button>
                </div>

                {/* OR divider */}
                <div className="flex items-center gap-4 py-2 pb-1">
                  <div className="flex-1 h-px bg-[#EAECF0]"></div>
                  <span className="text-sm text-[#667085] font-sfMedium">
                    OR
                  </span>
                  <div className="flex-1 h-px bg-[#EAECF0]"></div>
                </div>
              </>
            );
          })()}

        {/* Create new agenda form */}
        <div className="grid grid-cols-1 gap-4 w-full">
          <div>
            <label htmlFor="">Speaking format</label>

            <select
              onChange={(e) => {
                setEventProposal((prev) => ({
                  ...prev,
                  speakingFormat: e.target.value as AgendaType,
                }));
              }}
              className={
                "bg-[#F6F3EF] border  text-xs focus:border-[#F6F3EF] outline-none w-full lg:text-sm  p-3 rounded-lg"
              }
              value={eventProposal.speakingFormat}
            >
              {agendaTypes
                .filter((v) => v.toLocaleLowerCase() !== "break")
                .map((item) => {
                  return (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  );
                })}
            </select>
          </div>
          {/* Proposed topic */}
          <div>
            <GInput
              required
              inputValue={eventProposal.proposedTopic}
              setInput={(e) => {
                setEventProposal((prev) => ({ ...prev, proposedTopic: e }));
                if (e.trim()) setTopicError("");
              }}
              label="Proposed topic"
              placeholder="Enter proposed topic"
              error={topicError}
              inputBackgroundColor="#F5F5F5"
            />
          </div>

          {/* Description */}
          <div>
            <GTextArea
              inputValue={eventProposal.description}
              setInput={(e) =>
                setEventProposal((prev) => ({ ...prev, description: e }))
              }
              label="Description"
              placeholder="Enter description"
              inputBackgroundColor="#F5F5F5"
            />
          </div>

          {/* Duration */}
          <div>
            <GInput
              inputValue={
                eventProposal.duration > 0 ? String(eventProposal.duration) : ""
              }
              setInput={(e) => {
                const num = parseInt(e.replace(/[^0-9]/g, ""), 10);
                const val = isNaN(num) ? 0 : Math.max(1, num);
                setEventProposal((prev) => ({
                  ...prev,
                  duration: val,
                }));
                if (val > 600) {
                  setDurationError(
                    "Duration cannot exceed 10 hours (600 mins)",
                  );
                } else if (val > 0) {
                  setDurationError("");
                }
              }}
              label="Duration (mins)"
              placeholder="e.g. 30"
              error={durationError}
              inputBackgroundColor="#F5F5F5"
            />
          </div>
        </div>

        {/* Footer with Continue button */}
        <div className="pb-5 flex items-center gap-4">
          <button
            onClick={() => setFlow("inviteToSpeak")}
            className="w-full bg-[#EEEEEE] text-[#333333] rounded-lg py-2 font-sfMedium text-base hover:bg-[#3a3937] transition-colors hover:text-white"
          >
            Previous
          </button>
          <button
            onClick={() => {
              if (!eventProposal.proposedTopic.trim()) {
                setTopicError("Proposed topic is required");
                return;
              }
              if (!eventProposal.duration || eventProposal.duration < 1) {
                setDurationError("Duration is required");
                return;
              }
              if (eventProposal.duration > 600) {
                setDurationError("Duration cannot exceed 10 hours (600 mins)");
                return;
              }
              // Clear sessionId so the submit handler knows to create a new session
              setEventProposal((prev) => ({ ...prev, sessionId: "" }));
              setFlow("userFee");
            }}
            className="w-full bg-[#20242A] text-white rounded-lg py-2 font-sfMedium text-base hover:bg-[#3a3937] transition-colors"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function SelectAgenda({
  setFlow,
  eventData,
  setEventProposal,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
  eventData?: GetEventResponse;
}) {
  return (
    <div>
      <BackButton onClick={() => setFlow("agendaDetails")}></BackButton>

      <p className="mt-4 text-[#20242A] font-sfMedium text-base">
        Select an agenda
      </p>

      <div className="mt-4 grid grid-cols-1 gap-2">
        {(() => {
          const allSessions =
            eventData?.agenda.flatMap((agenda) => agenda.sessions) || [];
          if (allSessions.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center py-8">
                <CardsIcon stroke="#667085" />
                <p className="text-sm text-[#667085] font-sfRegular mt-3 text-center">
                  This event doesn't have any sessions yet.
                </p>
                <button
                  onClick={() => setFlow("agendaDetails")}
                  className="mt-4 text-sm text-primary font-sfMedium underline"
                >
                  Create a new session instead
                </button>
              </div>
            );
          }
          return allSessions.map((session) => (
            <AgendaCard
              key={session.id}
              setFlow={setFlow}
              session={session}
              setEventProposal={setEventProposal}
            />
          ));
        })()}
      </div>
    </div>
  );
}

function AgendaCard({
  setFlow,
  session,
  setEventProposal,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  session: AgendaInterface;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
}) {
  // Calculate duration in minutes
  const startTime = session.start ? dayjs(session.start).format("h:mma") : "--";
  const endTime = session.start
    ? dayjs(session.start).add(session.duration, "minute").format("h:mma")
    : "--";
  const duration = `${session.duration} mins`;
  const title = session.title;
  const description = session.description || "";

  return (
    <div
      className="p-3 bg-[#F5F5F5] rounded-md cursor-pointer transition-colors border border-transparent hover:border-[#FF692980] group"
      onClick={() => {
        setEventProposal((prev) => ({
          ...prev,
          sessionId: session.id,
          proposedTopic: session.title,
          description: session.description || "",
          duration: session.duration,
        }));
        setFlow("userFee");
      }}
    >
      <div className="flex items-center justify-between">
        {/* Icon */}
        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:hidden">
          <AwardBadgeIcon />
        </div>
        <div className="w-10 h-10 rounded-lg items-center justify-center flex-shrink-0 hidden group-hover:flex">
          <AwardBadgeFilledIcon />
        </div>

        {/* Time and Duration */}
        <div className="flex items-center gap-2 mb-2">
          <div className="flex items-center gap-1 text-sm text-[#444D5A] font-sfRegular">
            <ClockHistoryIcon />
            <span>
              {startTime} - {endTime}
            </span>
          </div>
          <span className="text-sm text-primary font-sfMedium underline-offset-1">
            {duration}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="min-w-0">
        {/* Title */}
        <h4 className="font-sfSemibold text-[#20242A] text-base mb-1 line-clamp-1">
          {title}
        </h4>

        {/* Description */}
        <p className="text-sm text-[#667085] font-sfRegular line-clamp-2">
          {description}
        </p>
      </div>
    </div>
  );
}

function UserFee({
  setFlow,
  previousFlow,
  setEventProposal,
  eventProposal,
  speaker,
  newEventData,
  eventData,
  setInvitationId,
}: {
  setFlow: Dispatch<SetStateAction<EventDetailsFlow>>;
  previousFlow: EventDetailsFlow | null;
  eventProposal: EventProposal;
  setEventProposal: Dispatch<SetStateAction<EventProposal>>;
  speaker?: Speaker;
  newEventData: NewEventData;
  eventData?: GetEventResponse;
  setInvitationId: Dispatch<SetStateAction<string>>;
}) {
  const { mutateAsync: inviteToSpeak } = useInviteToSpeak();
  const createEventMutation = useCreateEvent();
  const addSessionMutation = useAddSession();
  const publishEventMutation = usePublishEvent();
  const addLineupMutation = useAddLineup();
  const { data: teamData } = useGetMyTeam();
  const userInfo = getUserDetails();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState("");
  const currencyOptions: SelectProps["options"] = useMemo(() => {
    return currencyCodes.map((item) => ({
      value: item.code,
      search: `${item.code} ${item.currency}`,
      label: (
        <div className="flex items-center gap-2">
          <CurrencyFlag currency={item.code} size="sm" />
          <span className="font-medium">{item.code}</span>
          <span className="text-gray-500">{item.currency}</span>
        </div>
      ),
    }));
  }, []);

  const isNewEventFlow =
    newEventData.eventName !== "" && eventProposal.eventId === "";

  const handleSubmit = async () => {
    if (!speaker?.speakerId) {
      toast.error(
        "Speaker info is unavailable. Please close and reopen this drawer.",
      );
      return;
    }

    // if (
    //   speaker.minimumAmount &&
    //   eventProposal.proposedAmount < speaker.minimumAmount
    // ) {
    //   toast.error(
    //     `Minimum proposed amount is ${eventProposal.currency} ${speaker.minimumAmount.toLocaleString()}`,
    //   );
    //   return;
    // }

    setIsSubmitting(true);
    try {
      let finalEventId = eventProposal.eventId;
      let finalSessionId = eventProposal.sessionId;
      // Ensure a fresh default date if the user never picked one — guards against
      // a stale default if the drawer sat open for a while
      const eventDateForSubmit =
        newEventData.eventDate ||
        getDefaultStartTime(15).format("YYYY-MM-DDTHH:mm:ss");

      if (isNewEventFlow) {
        // Step 1: Create event
        setSubmitStep("Creating event...");
        const eventResult = await createEventMutation.mutateAsync({
          title: newEventData.eventName,
          description: eventProposal.description || newEventData.eventName,
          startDate: eventDateForSubmit,
          teamId: teamData?.id || "",
          location: newEventData.eventLocation,
          latitude: newEventData.googleCoordinates?.lat
            ? String(newEventData.googleCoordinates.lat)
            : "",
          longitude: newEventData.googleCoordinates?.long
            ? String(newEventData.googleCoordinates.long)
            : "",
          placeId: newEventData.placeId || "",
          eventLink: newEventData.eventLink || null,
          bannerUrl: getRandomEventBanner(),
        });

        finalEventId = eventResult.id;

        // Step 2: Add lineup
        setSubmitStep("Adding lineup...");
        const lineupFormData = new FormData();
        lineupFormData.append("Avatar", "");
        lineupFormData.append("Email", userInfo?.email || "");
        lineupFormData.append("Name", userInfo?.fullName || "");
        lineupFormData.append("Description", "Event owner");
        lineupFormData.append("Facebook", "");
        lineupFormData.append("Instagram", "");
        lineupFormData.append("LinkedIn", "");
        lineupFormData.append("Designation", "");
        lineupFormData.append("Company", "");
        lineupFormData.append("Youtube", "");
        lineupFormData.append("OtherPlatforms", "");

        await addLineupMutation.mutateAsync({
          eventId: eventResult.id,
          teamId: teamData?.id || "",
          formData: lineupFormData,
        });

        // Step 3: Add session/agenda
        setSubmitStep("Scheduling session...");
        const sessionFormData = new FormData();
        sessionFormData.append("EventId", eventResult.id);
        sessionFormData.append("TeamId", teamData?.id || "");
        sessionFormData.append(
          "Sessions[0].Title",
          eventProposal.proposedTopic || newEventData.eventName,
        );
        sessionFormData.append(
          "Sessions[0].Duration",
          String(eventProposal.duration),
        );
        sessionFormData.append("Sessions[0].CollaboratorId", "");
        sessionFormData.append("Sessions[0].Position", "0");
        sessionFormData.append(
          "Sessions[0].AgendaType",
          eventProposal.speakingFormat,
        );
        sessionFormData.append(
          "Sessions[0].Description",
          eventProposal.description || "",
        );
        sessionFormData.append("Sessions[0].Start", eventDateForSubmit);
        sessionFormData.append("Sessions[0].Debrief", "0");
        sessionFormData.append("Sessions[0].Speakers[0].id", speaker.speakerId);

        const sessionResult =
          await addSessionMutation.mutateAsync(sessionFormData);
        finalSessionId = sessionResult.sessions[0]?.id || "";

        // Step 4: Publish event
        setSubmitStep("Publishing event...");
        await publishEventMutation.mutateAsync({
          eventId: eventResult.id,
          teamId: teamData?.id || "",
        });
      } else if (eventProposal.eventId && !eventProposal.sessionId) {
        // Existing event + new agenda flow
        // User selected an existing event but created a new agenda via AgendaDetails
        setSubmitStep("Creating agenda...");
        const sessionFormData = new FormData();
        sessionFormData.append("EventId", eventProposal.eventId);
        sessionFormData.append("TeamId", teamData?.id || "");
        sessionFormData.append(
          "Sessions[0].Title",
          eventProposal.proposedTopic,
        );
        sessionFormData.append(
          "Sessions[0].Duration",
          String(eventProposal.duration),
        );
        sessionFormData.append("Sessions[0].CollaboratorId", "");
        sessionFormData.append("Sessions[0].Position", "0");
        sessionFormData.append(
          "Sessions[0].AgendaType",
          eventProposal.speakingFormat,
        );
        sessionFormData.append(
          "Sessions[0].Description",
          eventProposal.description || "",
        );
        sessionFormData.append(
          "Sessions[0].Start",
          eventData?.startDate || new Date().toISOString(),
        );
        sessionFormData.append("Sessions[0].Debrief", "0");
        sessionFormData.append("Sessions[0].Speakers[0].id", speaker.speakerId);

        const sessionResult =
          await addSessionMutation.mutateAsync(sessionFormData);
        finalSessionId = sessionResult.sessions[0]?.id || "";
      }

      // Call inviteToSpeak for all flows
      setSubmitStep("Sending invitation...");
      const inviteResult = await inviteToSpeak({
        speakerId: speaker.speakerId,
        ...eventProposal,
        eventId: finalEventId,
        sessionId: finalSessionId,
      });

      setInvitationId(inviteResult.invitationId);
      setFlow("success");
    } catch (e: any) {
      console.error("Error submitting invitation:", e);
      const errorMessage =
        e?.response?.data?.message ||
        "Failed to submit invitation. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setSubmitStep("");
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 gap-3 w-full">
        <div className="flex flex-col">
          <p
            className={`text-[#344054] mb-1 font-monaMedium relative text-xs lg:text-sm`}
          >
            Currency
          </p>
          <Select
            showSearch
            className="no-focus-select w-full outline-none h-[48px]"
            popupMatchSelectWidth={320}
            placeholder="Choose Currency"
            options={currencyOptions}
            value={eventProposal.currency}
            optionFilterProp="search"
            filterOption={(input, option) =>
              (option?.search as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
            onChange={(value) => {
              setEventProposal((prev) => ({
                ...prev,
                currency: value,
              }));
            }}
            optionLabelProp="label"
          />
        </div>

        {/* Proposed Amount */}
        <div className="flex flex-col">
          <p
            className={`text-[#344054] mb-1 font-monaMedium relative text-xs lg:text-sm`}
          >
            Proposed Amount
          </p>
          <div className="w-full flex gap-2 bg-[#F5F5F5] rounded-lg p-3 items-center">
            <input
              type="text"
              placeholder="0"
              value={
                eventProposal.proposedAmount
                  ? eventProposal.proposedAmount.toLocaleString()
                  : ""
              }
              onChange={(e) => {
                const numericValue = e.target.value.replace(/[^\d]/g, "");
                const value = numericValue ? parseInt(numericValue) : 0;
                setEventProposal((prev) => ({
                  ...prev,
                  proposedAmount: value,
                }));
              }}
              className="w-full h-9 bg-transparent px-4 text-[#333333] text-base font-sfRegular focus:outline-none"
            />
          </div>
          {speaker?.minimumAmount ? (
            <p className="text-base text-[#E3592E] font-sfRegular">
              Minimum Fee (Set by Speaker) {speaker.currency || "USD"}{" "}
              {speaker.minimumAmount.toLocaleString()}
            </p>
          ) : null}
        </div>

        {/* Additional note */}
        <div>
          <GTextArea
            inputValue={eventProposal.note}
            setInput={(e) => {
              setEventProposal((prev) => ({ ...prev, note: e }));
            }}
            label="Additional note"
            placeholder="Send a message to the speaker"
            inputBackgroundColor="#F5F5F5"
          />
        </div>
      </div>

      <div className="pb-5 flex items-center gap-4">
        <button
          disabled={isSubmitting}
          onClick={() => {
            // Users can only arrive at userFee from agendaDetails (new session)
            // or selectAgenda (existing session) — always land on one of those
            setFlow(
              previousFlow === "selectAgenda"
                ? "selectAgenda"
                : "agendaDetails",
            );
          }}
          className="w-full bg-[#EEEEEE] text-[#333333] rounded-lg py-2 font-sfMedium text-base hover:bg-[#3a3937] hover:text-white transition-colors disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-[#EEEEEE] disabled:hover:text-[#333333]"
        >
          Previous
        </button>
        <button
          disabled={isSubmitting}
          onClick={handleSubmit}
          className="w-full bg-[#20242A] text-white rounded-lg py-2 font-sfMedium text-base hover:bg-[#3a3937] transition-colors disabled:opacity-80 disabled:cursor-wait"
        >
          {isSubmitting ? submitStep || "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
}

function Success({
  onClose,
  invitationId,
}: {
  onClose: () => void;
  invitationId: string;
}) {
  return (
    <div>
      <div className="mt-[52px] ">
        <CheckCircleBadgeIcon />
        <h2 className="text-2xl font-sfMedium text-[#333333] mt-5">
          Invitation sent successfully
        </h2>

        <p className="my-3 text-base font-sfRegular text-[#545454]">
          Your speaking invitation has been sent to the speaker. You’ll be
          notified once they review and respond to the request.
        </p>
      </div>

      {/* Footer with Continue button */}
      <div className="pb-5 flex flex-col items-center gap-2">
        <button
          onClick={() => {
            onClose();
            window.location.assign(
              getAppUrl(`/speaking-invitations/${invitationId}`),
            );
          }}
          className="w-full bg-[#20242A] text-white rounded-lg py-2 font-sfMedium text-base hover:bg-[#3a3937] transition-colors"
        >
          View invitation
        </button>
      </div>
    </div>
  );
}

// Round "now" up to the next minuteStep boundary
function getDefaultStartTime(minuteStep: number = 15) {
  const now = dayjs().second(0).millisecond(0);
  const remainder = now.minute() % minuteStep;
  const delta = remainder === 0 ? 0 : minuteStep - remainder;
  return now.add(delta, "minute");
}
